import { BrightnessMedium } from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import MDEditor from '@uiw/react-md-editor';
import { Empty, Typography } from 'antd';
import BackIcon from 'assets/Icons/modals/BackIcon';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';
import ToggleSwitch from 'components/elements/Toggles/ToggleSwitch';
import DOMPurify from 'dompurify';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import rehypeSanitize from 'rehype-sanitize';
import AIDocumentationService, {
  BuildingDocumentationResponse,
  SetBuildingDocumentationRequest
} from 'services/hooks/ai-documentation-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import { colours } from 'utils/constants';

const LS_KEY_BUILDING = 'bl.aiassistant.settings.building';
const LS_KEY_STAFF_TOGGLE = 'bl.aiassistant.settings.staff';
enum OccupantType {
  Occupant = 'Occupant',
  Staff = 'Staff'
}
const defaultOccupantDocumentation = `# Occupants Documentation

### Contact Information
- **Building name**: Your building name
- **Address**: *123 Main Street, Suite 500, City, State, ZIP Code*
- **Phone:** (123) 456-7890
- **Email:** info@building.com

### Hours of Operation
- **Weekdays**: 8 AM - 6 PM
- **Weekends**: Closed

### Facilities
- Free Wi-Fi
- Pool
- Parking

### FAQ (Frequently Asked Questions)

#### What is the Wifi name & password?
- The Wifi network is FakeNetwork and the password is F4keP4ssw0rd

#### Where can I park?
- There is free parking behind the building in the daytime.
`;
const defaultStaffDocumentation = `# Staff Documentation

### General Policies
- **Dress code**: Business attire
- **Core Values**: Hospitality, Integrity, Teamwork, Professionalism.
- **Our website**: [Example Hotel](https://example.com/)

### Check-In and Check-Out Procedures

#### Check-In Process:
- Greet the guest with a smile and professional tone.
- Request ID and booking details.
- Confirm room type and explain available amenities (breakfast, Wi-Fi, gym, etc.).
- Process payment or hold deposit and issue room key/card.
- Provide guest with hotel map, and directions to their room, and highlight emergency exits.

### Check-Out Process:

#### Confirm guest’s name and room number
- Review the bill with the guest, noting any charges (mini-bar, room service).
- Process payment and issue receipt.
- Ask for feedback and offer assistance with transportation or luggage.
- Thank the guest and invite them to stay again.
`;

export default function AIAssistantSettings() {
  // get selected building and staff from localstorage
  const lsSelectedBuildingUuid = localStorage.getItem(LS_KEY_BUILDING);
  const lsStaff = localStorage.getItem(LS_KEY_STAFF_TOGGLE);
  const navigate = useNavigate();
  const [selectedBuildingUuid, setSelectedBuildingUuid] = useState<string>('');
  const [isDarkMode, setDarkMode] = useState<boolean>(false);
  const [isOccupantDirty, setOccupantDirty] = useState<boolean>(false);
  const [isStaffDirty, setStaffDirty] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [occupantDocumentation, setOccupantDocumentation] = useState<string | undefined>(
    defaultOccupantDocumentation
  );
  const [staffDocumentation, setStaffDocumentation] = useState<string | undefined>(
    defaultStaffDocumentation
  );
  const [isStaff, setStaff] = useState<boolean>(false);

  // Buildings query
  const { isLoading: isBuildingsLoading, data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error('Problem fetching buildings.');
      },
      refetchOnWindowFocus: false
    }
  );

  // Building AI Documentation query
  const { isLoading: isDocumentationLoading, data: documentationData } = useQuery<
    BuildingDocumentationResponse,
    Error
  >(
    ['buildingDocumentation', selectedBuildingUuid],
    () => AIDocumentationService.getBuildingDocumentation(selectedBuildingUuid),
    {
      enabled: !!selectedBuildingUuid,
      retry: false,
      onError: () => {
        // if there is no documentation found for the building, use the default
        setOccupantDocumentation(defaultOccupantDocumentation);
        setStaffDocumentation(defaultStaffDocumentation);
        setOccupantDirty(false);
        setStaffDirty(false);
      },
      refetchOnWindowFocus: false
    }
  );

  useEffect(() => {
    if (!!documentationData?.guest_information) {
      setOccupantDocumentation(documentationData.guest_information);
      setOccupantDirty(false);
    }
    if (!!documentationData?.staff_information) {
      setStaffDocumentation(documentationData.staff_information);
      setOccupantDirty(false);
    }
  }, [documentationData]);

  // Update the selected building when retrieved
  useEffect(() => {
    const selectedBuildingData = buildingsData?.find(
      (building) => building.uuid === lsSelectedBuildingUuid
    );
    // If no building is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!buildingsData &&
      !selectedBuildingUuid &&
      lsSelectedBuildingUuid &&
      selectedBuildingData
    ) {
      setSelectedBuildingUuid(lsSelectedBuildingUuid);
    }
  }, [buildingsData, lsSelectedBuildingUuid, selectedBuildingUuid]);

  // On component mount, update staff state based on local storage
  useEffect(() => {
    if (!!lsStaff) {
      setStaff(lsStaff == OccupantType.Staff);
    }
  }, [lsStaff]);

  const handleBuildingChange = (event: SelectChangeEvent) => {
    const buildingId = event.target.value as string;
    if (buildingId !== selectedBuildingUuid) {
      setSelectedBuildingUuid(buildingId);
      // store the last selected building in localstorage
      localStorage.setItem(LS_KEY_BUILDING, buildingId);
      setOccupantDirty(false);
      setStaffDirty(false);
    }
  };

  const handleDocumentationChange = (value?: string) => {
    if (!isSaving) {
      if (isStaff) {
        setStaffDocumentation(value);
        setStaffDirty(true);
      } else {
        setOccupantDocumentation(value);
        setOccupantDirty(true);
      }
    }
  };

  const handleStaffToggle = (staffToggleVal: string) => {
    setStaff(staffToggleVal == OccupantType.Staff);
    // update local storage
    localStorage.setItem(LS_KEY_STAFF_TOGGLE, staffToggleVal);
  };

  const handleSave = () => {
    // Sanitize content before saving it
    const cleanOccupantDoc = DOMPurify.sanitize(occupantDocumentation);
    const cleanStaffDoc = DOMPurify.sanitize(staffDocumentation);

    const payload: SetBuildingDocumentationRequest = {
      building_uuid: selectedBuildingUuid,
      guest_information: cleanOccupantDoc,
      staff_information: cleanStaffDoc
    };

    setSaving(true);
    AIDocumentationService.setBuildingDocumentation(payload)
      .then(() => {
        toast.success('Building documentation saved.');
        setOccupantDirty(false);
        setStaffDirty(false);
        setSaving(false);
      })
      .catch((error) => {
        toast.error('Problem saving building documentation. Please retry.');
        console.error('Problem saving building documentation: ', error);
        setSaving(false);
      });
  };

  return (
    <div style={{ paddingTop: 40, maxWidth: 1500 }}>
      <SecondaryButton
        buttonLabel="Back"
        iconLeft={<BackIcon />}
        onClick={() => navigate('/ai-assistant')}
        variant="default"
        size="px-4 w-auto"
        type="button"
      />

      <h1 style={{ paddingTop: 40 }}>
        Building Documentation for AI Assistant{' '}
        <HelpTooltipIcon
          useSubTag={false}
          height={30}
          message="Building information for your occupants using the AI Assistant on the b-line mobile app."
        />
      </h1>

      <Grid container justifyContent="space-between" alignItems="center" marginY={2}>
        <Grid item xs={true}>
          {/* buildings dropdown */}
          <FormControl fullWidth size="small" disabled={isBuildingsLoading}>
            <InputLabel>Building</InputLabel>
            <Select
              value={selectedBuildingUuid}
              sx={{ borderRadius: '10px', backgroundColor: 'white', maxWidth: 300 }}
              disabled={!buildingsData}
              onChange={handleBuildingChange}
              label="Building"
            >
              {!!buildingsData &&
                buildingsData
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((building) => {
                    return (
                      <MenuItem key={building.uuid} value={building.uuid}>
                        {building.name}
                      </MenuItem>
                    );
                  })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={true}>
          <span style={{ marginLeft: 10, marginRight: 10 }}>
            <ToggleSwitch
              option1={OccupantType.Occupant}
              option1Tooltip="Documentation available for your regular occupants"
              option2={OccupantType.Staff}
              option2Tooltip="Documentation only availble to your staff occupants"
              selectedOption={isStaff ? OccupantType.Staff : OccupantType.Occupant}
              onChange={handleStaffToggle}
            />
          </span>
        </Grid>
        <Grid item>
          <Box display="flex" alignItems="center">
            {/* color mode */}
            <Tooltip title="Light / Dark Editor">
              <IconButton
                sx={{ color: 'black' }}
                onClick={() => {
                  setDarkMode(!isDarkMode);
                }}
                disabled={!selectedBuildingUuid}
              >
                <BrightnessMedium />
              </IconButton>
            </Tooltip>
            {/* save button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
              sx={{
                marginLeft: '16px',
                backgroundColor: colours.seafoam,
                '&:hover': {
                  backgroundColor: colours.darkTeal
                }
              }}
              disabled={
                isBuildingsLoading ||
                (!isStaff && !isOccupantDirty) ||
                (isStaff && !isStaffDirty) ||
                !selectedBuildingUuid ||
                (!isStaff && !occupantDocumentation) ||
                (isStaff && !staffDocumentation) ||
                isDocumentationLoading ||
                isSaving
              }
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>

      {(isDocumentationLoading || isBuildingsLoading) && (
        <Skeleton variant="rectangular" height={800} style={{ marginTop: 40 }} />
      )}

      {!!selectedBuildingUuid && !isDocumentationLoading ? (
        <div style={{ marginTop: 40 }} className="container">
          {isSaving && <LinearProgress />}
          <div data-color-mode={isDarkMode ? 'dark' : 'light'}>
            <MDEditor
              height={800}
              value={isStaff ? staffDocumentation : occupantDocumentation}
              onChange={handleDocumentationChange}
              previewOptions={{
                rehypePlugins: [[rehypeSanitize]]
              }}
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center mt-32">
          <Empty
            description={
              <>
                <Typography.Text style={{ color: 'grey' }}>
                  Select a building to provide its documentation.
                </Typography.Text>
              </>
            }
          />
        </div>
      )}
    </div>
  );
}
